import React, { useState } from 'react';
import { Close, Email, Phone, CalendarToday, Security, Edit, Block, CheckCircle, Person, SupervisorAccount } from '@mui/icons-material';

export const MemberDetailsModal = ({ show, onClose, member, onChangeRole, onRevokeAccess, onReactivate }) => {
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);

    if (!show || !member) return null;

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'active': 'text-green-600 bg-green-100 border-green-200',
            'pending': 'text-amber-600 bg-amber-100 border-amber-200',
            'revoked': 'text-red-600 bg-red-100 border-red-200',
            'abandoned': 'text-gray-600 bg-gray-100 border-gray-200'
        };
        return colors[status] || 'text-gray-600 bg-gray-100 border-gray-200';
    };

    const getRoleColor = (role) => {
        return role === 'admin'
            ? 'bg-purple-100 text-purple-800 border-purple-200'
            : 'bg-blue-100 text-blue-800 border-blue-200';
    };

    const getRoleLabel = (role) => {
        return role === 'admin' ? 'Administrateur' : 'Caissier(e)';
    };

    const handleRoleChange = (newRole) => {
        onChangeRole(member.id, newRole);
        setShowRoleDropdown(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-6 border border-purple-200/70 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Détails du Membre</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <Close className="text-gray-500" />
                    </button>
                </div>

                <div className="space-y-6">
                    {/* En-tête */}
                    <div className="flex items-center space-x-4">
                        <div className="text-5xl">{member.avatar}</div>
                        <div>
                            <h4 className="text-xl font-bold text-gray-900">{member.name}</h4>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border mt-2 ${getStatusColor(member.status)}`}>
                                {member.status === 'active' ? 'Actif' :
                                    member.status === 'pending' ? 'En attente' :
                                        member.status === 'revoked' ? 'Accès révoqué' : 'Abandonné'}
                            </span>
                        </div>
                    </div>

                    {/* Informations de contact */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <Phone className="text-gray-400" />
                                <div>
                                    <div className="text-sm text-gray-600">Téléphone</div>
                                    <div className="font-medium text-gray-900">{member.phone}</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Email className="text-gray-400" />
                                <div>
                                    <div className="text-sm text-gray-600">Email</div>
                                    <div className="font-medium text-gray-900">{member.email}</div>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <CalendarToday className="text-gray-400" />
                                <div>
                                    <div className="text-sm text-gray-600">Membre depuis</div>
                                    <div className="font-medium text-gray-900">{formatDate(member.joinDate)}</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <CalendarToday className="text-gray-400" />
                                <div>
                                    <div className="text-sm text-gray-600">Dernière activité</div>
                                    <div className="font-medium text-gray-900">{formatDate(member.lastActive)}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rôle et permissions */}
                    <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                            <h5 className="font-semibold text-gray-900 flex items-center space-x-2">
                                <Security />
                                <span>Rôle et Permissions</span>
                            </h5>
                            {member.status === 'active' && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                                        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-xl border-2 transition-all duration-300 ${getRoleColor(member.role)} hover:shadow-lg`}
                                    >
                                        <Edit className="text-sm" />
                                        <span>Modifier le rôle</span>
                                    </button>

                                    {showRoleDropdown && (
                                        <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-2xl z-10">
                                            <button
                                                onClick={() => handleRoleChange('cashier')}
                                                className="flex items-center space-x-3 w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100"
                                            >
                                                <Person className="text-blue-500" />
                                                <div>
                                                    <div className="font-medium">Caissier(e)</div>
                                                    <div className="text-sm text-gray-600">Accès limité</div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => handleRoleChange('admin')}
                                                className="flex items-center space-x-3 w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors"
                                            >
                                                <SupervisorAccount className="text-purple-500" />
                                                <div>
                                                    <div className="font-medium">Administrateur</div>
                                                    <div className="text-sm text-gray-600">Accès complet</div>
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center space-x-3 mb-4">
                            <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${getRoleColor(member.role)}`}>
                                {getRoleLabel(member.role)}
                            </span>
                        </div>

                        {/* Liste des permissions */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">Permissions accordées:</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {member.permissions.map((permission, index) => (
                                    <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <CheckCircle className="text-green-500 text-sm" />
                                        <span className="text-sm text-gray-700">{permission}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Historique d'activité */}
                    <div className="border-t pt-6">
                        <h5 className="font-semibold text-gray-900 mb-4">Historique Récent</h5>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center text-sm p-3 bg-blue-50 rounded-lg">
                                <span className="text-blue-700">Invitation envoyée</span>
                                <span className="text-blue-600">{formatDate(member.joinDate)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm p-3 bg-green-50 rounded-lg">
                                <span className="text-green-700">Dernière connexion</span>
                                <span className="text-green-600">{formatDate(member.lastActive)}</span>
                            </div>
                            {member.status === 'revoked' && (
                                <div className="flex justify-between items-center text-sm p-3 bg-red-50 rounded-lg">
                                    <span className="text-red-700">Accès révoqué</span>
                                    <span className="text-red-600">{formatDate(member.lastActive)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-3 pt-6 border-t">
                        {member.status === 'active' && (
                            <button
                                onClick={() => onRevokeAccess(member)}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl hover:from-red-600 hover:to-orange-600 transition-all duration-300"
                            >
                                <Block className="text-sm" />
                                <span className="text-sm font-semibold">Révoquer l'Accès</span>
                            </button>
                        )}

                        {(member.status === 'revoked' || member.status === 'abandoned') && (
                            <button
                                onClick={() => onReactivate(member)}
                                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300"
                            >
                                <CheckCircle className="text-sm" />
                                <span className="text-sm font-semibold">Réactiver</span>
                            </button>
                        )}

                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-gray-500/10 text-gray-700 rounded-xl hover:bg-gray-500/20 transition-all duration-300 border border-gray-300/50 font-semibold"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};